import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CreateUser, EditUser } from '../../../services/users';
import useSnackbar from '../../../hooks/useSnackbar';
import { CustomerType } from '../../../utils/entities/type';
import { IUser } from '../../../utils/entities/interface';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';

interface FormData {
  id?: string;
  fullName: string;
  phone: string;
  email: string;
  type: CustomerType;
}

interface UserAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  customer?: IUser | null;
}
const CreateUserModal = ({
  open,
  onClose,
  onSaved,
  customer,
}: UserAddEditProps) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    type: 'RETAIL',
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        id: customer._id,
        fullName: customer.name || '',
        phone: customer.phoneNumber || '',
        email: customer.email || '',
        type: customer.type || 'RETAIL',
      });
    } else {
      setFormData({ fullName: '', phone: '', email: '', type: 'RETAIL' });
    }
  }, [customer]);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      let status;
      if (formData.id) {
        status = await EditUser({
          phoneNumber: formData.phone,
          countryCode: '+91',
          userName: formData.fullName,
          type: formData.type,
          email: formData.email,
          userId: formData.id,
        });
      } else {
        status = await CreateUser({
          phoneNumber: formData.phone,
          countryCode: '+91',
          userName: formData.fullName,
          type: formData.type,
          email: formData.email,
        });
      }

      if (status === 200 || status === 201) {
        showSnackbar(
          `User ${formData.id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setTimeout(() => {
          onClose();
          if (onSaved) onSaved();
        }, 1500);
      } else {
        showSnackbar(
          `Error: Failed to ${formData.id ? 'update' : 'create'} user`,
          'error',
        );
      }
    } catch (error: any) {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'hidden', // Ensures the background doesn't leak
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #E31E25', // Add border here
          borderRadius: 10,
          overflow: 'hidden', // Prevent border from affecting layout
        }}
      >
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#181818',
            padding: '16px',
          }}
        >
          {formData.id ? 'Edit User' : 'Create User'}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography gutterBottom>Full Name</Typography>
              <TextField
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                fullWidth
                required
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom> Contact Number</Typography>
              <TextField
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ maxLength: 10, pattern: '[0-9]*' }}
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Email Address</Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Customer Type</Typography>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              >
                <MenuItem value="RETAIL">Retail</MenuItem>
                <MenuItem value="HNI">HNI</MenuItem>
                <MenuItem value="SME">SME</MenuItem>
                <MenuItem value="LARGE_SCALE">Large Scale</MenuItem>
                <MenuItem value="OEM">OEM</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <SecondaryButton title="Cancel" onClick={onClose} />
            <PrimaryButton title="Save" onClick={(e) => handleSubmit(e)} />
          </DialogActions>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateUserModal;
